// ═══════════════════════════════════════════════════════════════════════════
//  SIMPANAN LOKAL LAGU DI PC/TV ROOM (6 Okt 2026)
//
//  Zaldy: "kl pake internet aga lemot" → lagu Top ElGroup & yang sering
//  diputar di outlet ini diunduh ke disk PC room (Cache Storage Chrome) saat
//  jam sepi; public/sw-media.js menyajikannya dari disk saat diputar. Yang
//  belum tersimpan tetap streaming.
//
//  Unduhan dijalankan DARI HALAMAN pemutar (bukan dari service worker): SW
//  dimatikan peramban sesudah beberapa menit, sedangkan mengunduh ratusan lagu
//  makan berjam-jam. Halaman pemutar di PC room memang terbuka terus.
//
//  Hanya aktif bila berkas lagu diambil dari server pusat (/api/media). Outlet
//  yang sudah punya server lagu LAN tidak butuh ini.
// ═══════════════════════════════════════════════════════════════════════════
import axios from 'axios'

const CACHE = 'kr-media-v1'
const KUNCI_SETELAN = 'karaoke.simpan'

export type SetelanSimpan = { aktif: boolean; jumlah: number; jamMulai: number; jamSelesai: number }
export const setelanBawaan: SetelanSimpan = { aktif: true, jumlah: 150, jamMulai: 5, jamSelesai: 16 }

export function bacaSetelanSimpan(): SetelanSimpan {
  try { return { ...setelanBawaan, ...JSON.parse(localStorage.getItem(KUNCI_SETELAN) || '{}') } } catch { return setelanBawaan }
}
export function simpanSetelanSimpan(s: SetelanSimpan) {
  try { localStorage.setItem(KUNCI_SETELAN, JSON.stringify(s)) } catch { /* */ }
}

export type KeadaanSimpan = {
  didukung: boolean
  jumlah: number      // berkas tersimpan
  byte: number
  sisaByte: number | null
  unduh: { selesai: number; total: number } | null
  pesan: string
}

const pendengar = new Set<(k: KeadaanSimpan) => void>()
let keadaan: KeadaanSimpan = { didukung: false, jumlah: 0, byte: 0, sisaByte: null, unduh: null, pesan: '' }
function ubah(p: Partial<KeadaanSimpan>) { keadaan = { ...keadaan, ...p }; pendengar.forEach(f => f(keadaan)) }
export function langgananSimpan(f: (k: KeadaanSimpan) => void) { pendengar.add(f); f(keadaan); return () => { pendengar.delete(f) } }

const didukung = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'caches' in window

/** Pasang service worker + minta penyimpanan permanen (tidak dibersihkan otomatis). */
export async function pasangSimpanan(): Promise<boolean> {
  if (!didukung()) { ubah({ didukung: false, pesan: 'Peramban tidak mendukung simpanan lokal' }); return false }
  try {
    await navigator.serviceWorker.register('/sw-media.js', { scope: '/' })
    try { await navigator.storage?.persist?.() } catch { /* */ }
    ubah({ didukung: true })
    await segarkanStatus()
    return true
  } catch (e) {
    ubah({ didukung: false, pesan: 'Service worker gagal dipasang: ' + (e as Error).message })
    return false
  }
}

export async function segarkanStatus() {
  if (!didukung()) return
  const c = await caches.open(CACHE)
  const ks = await c.keys()
  let byte = 0
  for (const k of ks) {
    const r = await c.match(k)
    byte += parseInt(r?.headers.get('Content-Length') || '0', 10) || 0
  }
  let sisa: number | null = null
  try {
    const est = await navigator.storage.estimate()
    if (est.quota != null && est.usage != null) sisa = est.quota - est.usage
  } catch { /* */ }
  ubah({ jumlah: ks.length, byte, sisaByte: sisa })
}

const alamatMedia = (p: string) => `${location.origin}/api/media/${encodeURIComponent(p.replace(/^\/+/, ''))}`

type BerkasSimpan = { path: string; ukuran_byte: number | null }

let berjalan = false

/** Satu putaran: ambil daftar dari server, rapikan yang tidak masuk daftar,
 *  unduh yang belum ada — berurutan, satu per satu. */
export async function putaranSimpan(token: string, opsi?: { paksa?: boolean; sibuk?: () => boolean }) {
  if (berjalan || !didukung()) return
  const s = bacaSetelanSimpan()
  if (!s.aktif && !opsi?.paksa) return
  const jam = new Date().getHours()
  const diJamSepi = s.jamMulai <= s.jamSelesai
    ? jam >= s.jamMulai && jam < s.jamSelesai
    : jam >= s.jamMulai || jam < s.jamSelesai
  if (!diJamSepi && !opsi?.paksa) return
  berjalan = true
  try {
    // Token perangkat (TV/PC yang dipasangkan ke room) — bukan kode sesi:
    // pengunduhan terjadi pagi hari, saat room belum punya sesi.
    const r = await axios.get<BerkasSimpan[]>(`/api/t/perangkat/${encodeURIComponent(token)}/simpan-lokal`,
      { params: { n: s.jumlah } })
    const daftar = Array.from(new Set((r.data ?? []).map(b => alamatMedia(b.path))))
    const c = await caches.open(CACHE)
    // Yang sudah tidak masuk daftar dihapus — disk PC tidak terus membengkak.
    const tetap = new Set(daftar)
    for (const k of await c.keys()) if (!tetap.has(k.url)) await c.delete(k)

    let selesai = 0
    for (const url of daftar) {
      if (await c.match(url)) { selesai++; continue }
      // Jangan bersaing dengan lagu yang sedang diputar.
      while (opsi?.sibuk?.()) await new Promise(res => setTimeout(res, 30_000))
      try {
        const est = await navigator.storage.estimate()
        if (est.quota && est.usage && est.quota - est.usage < 2 * 1024 ** 3) {
          ubah({ pesan: 'Ruang disk PC hampir penuh — unduhan berhenti' }); break
        }
      } catch { /* */ }
      try {
        const res = await fetch(url, { cache: 'no-store' })
        if (res.ok && res.status === 200) {
          const blob = await res.blob()
          await c.put(url, new Response(blob, {
            headers: {
              'Content-Type': res.headers.get('Content-Type') || 'application/octet-stream',
              'Content-Length': String(blob.size),
              'Accept-Ranges': 'bytes',
            },
          }))
          selesai++
        }
      } catch { /* jaringan putus — dicoba lagi di putaran berikutnya */ }
      ubah({ unduh: { selesai, total: daftar.length } })
    }
    ubah({ unduh: null, pesan: '' })
  } catch (e) {
    ubah({ unduh: null, pesan: 'Daftar simpanan gagal diambil: ' + (e as Error).message })
  } finally {
    berjalan = false
    await segarkanStatus()
  }
}

export const ukuranTeks = (b: number) =>
  b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(1)} GB` : `${Math.round(b / 1024 ** 2)} MB`
